import moment from 'moment-timezone';
import { WINDOW_TYPE } from 'src/app/MyQuote/AddQuote/Steps/addQuoteSteps.const';
import { GetAppearance, getDateWithSuffixFormat, getFormattedDateIgnoringTime } from 'src/utils/CommonFunctions';

/**
 * Schedule display helpers — ported from wal-frontend-v2 so the customer portal formats
 * pickup & delivery scheduling exactly the way the internal Radar does.
 *
 * The backend stores split columns per leg:
 *   currentPickupStartDate / currentPickupStartTime / ...Sensitivity / ...WindowType
 *   currentDeliveryStartDate / currentDeliveryStartTime / ...Sensitivity / ...WindowType
 * The date/time are naked local wall-clock values; the tz column gives the IANA zone.
 */

type ScheduleType = 'pickup' | 'delivery';

type DateType = 'open' | 'fixed' | 'flexible';

export type WindowType = 'open' | 'ready_time' | 'appointment' | 'delivery_time';

interface IScheduleFields {
    startDate: string | null; // YYYY-MM-DD (local wall-clock)
    endDate: string | null;
    startTime: string | null; // HH:MM:SS (local wall-clock)
    endTime: string | null;
    sensitivity: DateType | null;
    windowType: WindowType | null;
    timezone: string | null; // IANA
}

export interface IScheduleDisplay {
    hasData: boolean;
    month: string; // "SEP"
    day: string; // "16"
    formattedDate: string; // "Tuesday, Sep 16th, 2025"
    formattedDateRange: string; // "Sep 16 - Sep 19, 2025" for flexible, else same as formattedDate
    timeRange: string; // "07:00 - 15:00" (stored wall-clock)
    timeRangeLocal: string; // same moment, expressed in user's tz
    timezoneLabel: string; // "America/Toronto"
    hasTimeWindow: boolean;
    hasDateWindow: boolean;
    windowLabel: string; // "Ready Time" / "Delivery Time" / "Appointment" / "Open"
}

/**
 * Normalize a raw source into our canonical shape. Accepts a raw `order_leg_schedule`
 * row (split `currentPickup*` / `currentDelivery*` columns) or a flat prefixed/unprefixed object.
 */
export const extractSchedulePayload = (source: any, type: ScheduleType): IScheduleFields | null => {
    if (!source) return null;

    // Detect a raw `order_leg_schedule` row by the presence of the split date columns.
    const isRawRow = source.currentPickupStartDate !== undefined || source.currentDeliveryStartDate !== undefined;

    if (isRawRow) {
        const prefix = type === 'pickup' ? 'currentPickup' : 'currentDelivery';

        return {
            startDate: source[`${prefix}StartDate`] ?? null,
            endDate: source[`${prefix}EndDate`] ?? null,
            startTime: source[`${prefix}StartTime`] ?? null,
            endTime: source[`${prefix}EndTime`] ?? null,
            sensitivity: source[`${prefix}Sensitivity`] ?? null,
            windowType: source[`${prefix}WindowType`] ?? null,
            timezone: type === 'pickup' ? source.pickupTimezone ?? null : source.deliveryTimezone ?? null,
        };
    }

    const p = type === 'pickup' ? 'pickup' : 'delivery';

    return {
        startDate: source[`${p}StartDate`] ?? source.startDate ?? null,
        endDate: source[`${p}EndDate`] ?? source.endDate ?? null,
        startTime: source[`${p}StartTime`] ?? source.startTime ?? null,
        endTime: source[`${p}EndTime`] ?? source.endTime ?? null,
        sensitivity: source[`${p}Sensitivity`] ?? source.sensitivity ?? null,
        windowType: source[`${p}WindowType`] ?? source.windowType ?? null,
        timezone: source[`${p}Timezone`] ?? source.timezone ?? null,
    };
};

/** Trim seconds off a stored HH:MM:SS — returns HH:MM for display. */
const trimSeconds = (time: string | null): string => {
    if (!time) return '';

    return time.length >= 5 ? time.substring(0, 5) : time;
};

const WINDOW_LABEL_MAP: Record<WindowType, string> = {
    open: 'Open',
    ready_time: 'Ready Time',
    appointment: 'Appointment',
    delivery_time: 'Delivery Time',
};

/**
 * Convert a stored wall-clock HH:MM(:SS) from the shipper/consignee timezone into the viewing
 * user's timezone. Used only for the "Local Time" row — the main "Ready/Delivery Time" still
 * renders the shipper's wall-clock as-is.
 */
const convertTimeToUserTz = (date: string | null, time: string | null, sourceTimezone: string | null, userTimezone: string | null): string => {
    const trimmed = time && time.length >= 5 ? time.substring(0, 5) : time || '';
    if (!date || !time || !sourceTimezone || !userTimezone || sourceTimezone === userTimezone) return trimmed;
    const m = moment.tz(`${moment.utc(date).format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm:ss', sourceTimezone).tz(userTimezone);

    return m.isValid() ? m.format('HH:mm') : trimmed;
};

/**
 * Format the canonical schedule payload into display-ready strings.
 * The stored date/time are already in the shipper/consignee wall-clock timezone — main display
 * renders them as-is. `timeRangeLocal` is the only field converted to the user's viewing timezone.
 */
export const formatScheduleDisplay = (schedule: IScheduleFields | null, userTimezone?: string | null, dateFormat = 'MMM Do, YYYY'): IScheduleDisplay => {
    const empty: IScheduleDisplay = {
        hasData: false,
        month: '-',
        day: '-',
        formattedDate: '-',
        formattedDateRange: '-',
        timeRange: '-',
        timeRangeLocal: '-',
        timezoneLabel: '-',
        hasTimeWindow: false,
        hasDateWindow: false,
        windowLabel: '-',
    };
    if (!schedule || !schedule.startDate) return empty;

    // PG DATE columns come back from the backend as ISO timestamps at midnight UTC
    // (e.g. "2026-04-29T00:00:00.000Z"). Parsing with moment() interprets them in the
    // browser's local tz and can shift the calendar date (midnight UTC → Apr 28 20:00 EDT).
    // Use moment.utc so the date components match the calendar date the user entered.
    const startMoment = moment.utc(schedule.startDate);
    const endMoment = schedule.endDate ? moment.utc(schedule.endDate) : null;

    const hasDateWindow = !!(endMoment && endMoment.isValid() && !endMoment.isSame(startMoment, 'day'));
    const hasTimeWindow = !!(schedule.startTime && schedule.endTime);

    const startTimeFmt = trimSeconds(schedule.startTime);
    const endTimeFmt = trimSeconds(schedule.endTime);

    // Single-day header ("Today, Apr 21st, 2026" / "Monday, Apr 24th, 2026").
    const formattedDate = getDateWithSuffixFormat(getFormattedDateIgnoringTime(schedule.startDate, dateFormat, true, false, false, false));

    // Range header: each side substitutes Today/Yesterday/Tomorrow when applicable.
    const formattedDateRange =
        hasDateWindow && endMoment
            ? `${getDateWithSuffixFormat(getFormattedDateIgnoringTime(schedule.startDate, dateFormat, true, true, true, true))} - ${getDateWithSuffixFormat(
                  getFormattedDateIgnoringTime(schedule.endDate, dateFormat, true, true, true, true)
              )}`
            : formattedDate;

    const timeRange = hasTimeWindow && startTimeFmt && endTimeFmt ? `${startTimeFmt} - ${endTimeFmt}` : startTimeFmt || '-';

    // Local Time row — convert shipper/consignee wall-clock → viewing user's timezone.
    const startLocal = convertTimeToUserTz(schedule.startDate, schedule.startTime, schedule.timezone, userTimezone || null);
    const endLocal = convertTimeToUserTz(schedule.endDate || schedule.startDate, schedule.endTime, schedule.timezone, userTimezone || null);
    const timeRangeLocal = hasTimeWindow && startLocal && endLocal ? `${startLocal} - ${endLocal}` : startLocal || timeRange;

    return {
        hasData: true,
        month: startMoment.format('MMM').toUpperCase(),
        day: startMoment.format('DD'),
        formattedDate,
        formattedDateRange,
        timeRange,
        timeRangeLocal,
        timezoneLabel: schedule.timezone || '-',
        hasTimeWindow,
        hasDateWindow,
        windowLabel: (schedule.windowType && WINDOW_LABEL_MAP[schedule.windowType]) || '-',
    };
};

/** Convenience — one-call combo for when you just need the display object. */
export const getScheduleDisplay = (source: any, type: ScheduleType, userTimezone?: string | null, dateFormat?: string): IScheduleDisplay =>
    formatScheduleDisplay(extractSchedulePayload(source, type), userTimezone, dateFormat);

/**
 * Combine a stored date + time into a naked "YYYY-MM-DD HH:mm:ss" string.
 * Split columns already hold local wall-clock values, so we just concatenate.
 */
const joinDateTime = (date: string | null | undefined, time: string | null | undefined): string | null => {
    if (!date) return null;
    const datePart = String(date).split('T')[0];
    const timeStr = time ? String(time) : '';
    const timePart = timeStr ? (timeStr.length >= 8 ? timeStr.substring(0, 8) : `${timeStr}:00`.substring(0, 8)) : '00:00:00';

    return `${datePart} ${timePart}`;
};

/**
 * Human-readable duration between two wall-clock datetimes — "3 days" / "8 hours" / "45 minutes".
 * Returns '-' when either side is missing or the span is not positive.
 */
const getDiffTransitTimeDisplay = (startDate: string | null | undefined, endDate: string | null | undefined): string => {
    if (!startDate || !endDate) return '-';

    const start = moment.utc(startDate);
    const end = moment.utc(endDate);

    const transitDays = end.diff(start, 'days');

    if (transitDays > 0) {
        return `${transitDays} day${transitDays !== 1 ? 's' : ''}`;
    }

    const transitHours = end.diff(start, 'hours');

    if (transitHours > 0) {
        return `${transitHours} hour${transitHours !== 1 ? 's' : ''}`;
    }

    const transitMinutes = end.diff(start, 'minutes');

    if (transitMinutes > 0) {
        return `${transitMinutes} minute${transitMinutes !== 1 ? 's' : ''}`;
    }

    return '-';
};

/**
 * Compute the transit time as a range when either pickup or delivery has a date/time window.
 *
 *   min = pickupStart → deliveryStart
 *   max = pickupEnd   → deliveryEnd
 *
 * Output rules:
 *   - Same unit on both sides  → "24-25 days"
 *   - Different units          → "6 hours - 2 days"
 *   - min === max              → single value (e.g. "24 days")
 *   - Missing dates / 0        → "-"
 */
export const getTransitTimeRangeDisplay = (pickupDeliverySchedule: any): string => {
    const pickup = extractSchedulePayload(pickupDeliverySchedule, 'pickup');
    const delivery = extractSchedulePayload(pickupDeliverySchedule, 'delivery');

    if (!pickup?.startDate || !delivery?.startDate) return '-';

    const pickupStartDT = joinDateTime(pickup.startDate, pickup.startTime);
    const deliveryStartDT = joinDateTime(delivery.startDate, delivery.startTime);
    const pickupEndDT = joinDateTime(pickup.endDate || pickup.startDate, pickup.endTime || pickup.startTime);
    const deliveryEndDT = joinDateTime(delivery.endDate || delivery.startDate, delivery.endTime || delivery.startTime);

    if (!pickupStartDT || !deliveryStartDT || !pickupEndDT || !deliveryEndDT) return '-';

    // Raw durations (in minutes) so the range always renders lower → upper regardless of
    // which candidate produced the smaller value.
    const startPairMinutes = moment.utc(deliveryStartDT).diff(moment.utc(pickupStartDT), 'minutes');
    const endPairMinutes = moment.utc(deliveryEndDT).diff(moment.utc(pickupEndDT), 'minutes');

    const [lowerPair, upperPair] =
        startPairMinutes <= endPairMinutes
            ? [
                  { start: pickupStartDT, end: deliveryStartDT },
                  { start: pickupEndDT, end: deliveryEndDT },
              ]
            : [
                  { start: pickupEndDT, end: deliveryEndDT },
                  { start: pickupStartDT, end: deliveryStartDT },
              ];

    const lowerDisplay = getDiffTransitTimeDisplay(lowerPair.start, lowerPair.end);
    const upperDisplay = getDiffTransitTimeDisplay(upperPair.start, upperPair.end);

    if (lowerDisplay === upperDisplay) return lowerDisplay;

    // When the lower bound has no positive duration, collapse the range and show only the
    // upper value — e.g. "- - 8 hours" should render as "8 hours".
    if (lowerDisplay === '-') return upperDisplay;

    const unitRegex = /^(\d+)\s+(minute|minutes|hour|hours|day|days)$/;
    const lowerMatch = lowerDisplay.match(unitRegex);
    const upperMatch = upperDisplay.match(unitRegex);

    if (lowerMatch && upperMatch) {
        const lowerUnitBase = lowerMatch[2].replace(/s$/, '');
        const upperUnitBase = upperMatch[2].replace(/s$/, '');

        if (lowerUnitBase === upperUnitBase) {
            // Same unit — force plural since upper count differs from lower
            return `${lowerMatch[1]}-${upperMatch[1]} ${upperUnitBase}s`;
        }
    }

    return `${lowerDisplay} - ${upperDisplay}`;
};

/**
 * Countdown ("23 hours 59 minutes", "1 minute") for a future local-timezone start date.
 * Empty string when the date is missing, already past, or more than 24 hours away.
 * The `t` translator is passed in so unit labels come from i18n.
 */
export const getTimeUntilStart = (startDate: string, t: (key: string) => string): string => {
    if (!startDate) return '';

    const appearance = GetAppearance();
    const userTimezone = appearance?.timezone || 'UTC';

    const start = moment.tz(startDate, 'YYYY-MM-DD HH:mm:ss', userTimezone);
    if (!start.isValid()) return '';

    const now = moment.utc();
    const diffInMinutes = start.utc().diff(now, 'minutes');

    if (diffInMinutes <= 0) return '';
    if (diffInMinutes >= 24 * 60) return '';

    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;

    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? t('Common.Hour') : t('Common.Hours')}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? t('Common.Minute') : t('Common.Minutes')}`);

    return parts.join(' ');
};

// ─── Table-cell formatters (ported verbatim from wal-frontend-v2 schedule.helpers) ──────────────

/**
 * Format a scheduled pickup/delivery date (or flexible range) for list/table cells.
 * The year appears once at the end of a range — "Apr 30th - May 1st, 2026"; single: "Apr 30th, 2026".
 * Returns HTML (<sup> ordinals via getDateWithSuffixFormat) — render with dangerouslySetInnerHTML.
 * Dates are plain YYYY-MM-DD strings from the BE split columns — no tz conversion.
 */
export const formatScheduleDateRange = (startDate?: string | null, endDate?: string | null): string => {
    if (!startDate) return '-';
    const hasRange = !!endDate && endDate !== startDate;
    const startFmt = getDateWithSuffixFormat(getFormattedDateIgnoringTime(startDate, hasRange ? 'MMM Do' : 'MMM Do, YYYY', false));
    if (!hasRange) return startFmt;
    const endFmt = getDateWithSuffixFormat(getFormattedDateIgnoringTime(endDate!, 'MMM Do, YYYY', false));

    return `${startFmt} - ${endFmt}`;
};

/**
 * Plain-text variant of formatScheduleDateRange (no <sup> tags) — use as the TruncateTextCmp
 * tooltip message so the hover text is readable when the cell truncates with an ellipsis.
 */
const formatScheduleDateRangePlain = (startDate?: string | null, endDate?: string | null): string => {
    if (!startDate) return '-';
    const hasRange = !!endDate && endDate !== startDate;
    const startFmt = getFormattedDateIgnoringTime(startDate, hasRange ? 'MMM Do' : 'MMM Do, YYYY', false) || '-';
    if (!hasRange) return startFmt;
    const endFmt = getFormattedDateIgnoringTime(endDate!, 'MMM Do, YYYY', false) || '-';

    return `${startFmt} - ${endFmt}`;
};

interface IScheduleColumnValues {
    /** HTML with <sup> ordinals — render via dangerouslySetInnerHTML. */
    date: string;
    /** Plain-text date for tooltips. */
    datePlain: string;
    /** "01:00 - 02:00", "10:00" or "-". */
    time: string;
    /** "Business Hours" | "Window" | "Appointment" | "-". */
    type: string;
}

/**
 * Values for the six Pickup|Delivery Date / Type / Time table columns — the same derivation WAL
 * Radar's row uses (TableViewRow buildScheduleColumns), so the portal's Track and Orders tables
 * read identically to WAL:
 *   type: windowType=open → "Business Hours" (it has no start/end time, so it must be mapped
 *         BEFORE the window-vs-appointment fallback); else start+end time → "Window"; else "Appointment".
 *   date: "Sep 28th, 2026" or a flexible range "Sep 18th - Sep 19th, 2026".
 *   time: the wall-clock window "01:00 - 02:00" / single "10:00" / "-".
 * `source` is any object carrying the split columns (radar `schedule`, orders `pickupDeliverySchedule`).
 */
export const getScheduleColumnValues = (source: any, type: ScheduleType, t: (key: string) => string): IScheduleColumnValues => {
    const fields = extractSchedulePayload(source, type);
    const display = formatScheduleDisplay(fields, GetAppearance()?.timezone ?? null);

    if (!fields || !display.hasData) return { date: '-', datePlain: '-', time: '-', type: '-' };

    const resolveType = () => {
        if (fields.windowType === WINDOW_TYPE.OPEN) return t('Radar.Business_Hours');

        return display.hasTimeWindow ? t('Radar.Window') : t('Radar.Appointment');
    };

    return {
        date: formatScheduleDateRange(fields.startDate, fields.endDate),
        datePlain: formatScheduleDateRangePlain(fields.startDate, fields.endDate),
        time: display.timeRange || '-',
        type: resolveType(),
    };
};
